import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantCapsProvider } from "@/contexts/TenantCapsContext";
import { isAdmin, isDirectorOrAbove } from "@/lib/roles";
import { postLoginDestination } from "@/lib/postLoginRoute";
import { TrainingModeProvider } from "@/contexts/TrainingModeContext";
import { TrainingModeBanner } from "@/components/TrainingModeBanner";
import { AiAssistant } from "@/components/AiAssistant";
import { OnlineStatusBar } from "@/components/OnlineStatusBar";
import { FeedbackFab } from "@/components/FeedbackFab";
import Login from "@/pages/Login";
import DemoTour from "@/pages/DemoTour";
import RegisterProfile from "@/pages/RegisterProfile";
import RegisterPendingAdmin from "@/pages/RegisterPendingAdmin";
import MagicLinkLogin from "@/pages/MagicLinkLogin";
import SelectPlan from "@/pages/SelectPlan";
import ConfirmEmail from "@/pages/ConfirmEmail";
import MyProfile from "@/pages/MyProfile";
import Dashboard from "@/pages/Dashboard";
import SuperAdminTenants from "@/pages/SuperAdminTenants";
import BackOffice from "@/pages/BackOffice";
import InstallerJobs from "@/pages/InstallerJobs";
import AdminWpComponents from "@/pages/AdminWpComponents";
import AdminSystemHealth from "@/pages/AdminSystemHealth";
import InstallerOnboarding from "@/pages/InstallerOnboarding";
import { InstallerAuctionsList, InstallerAuctionDetail } from "@/pages/InstallerAuctions";
import AdminInstallerApplications from "@/pages/AdminInstallerApplications";
import AdminPenalties from "@/pages/AdminPenalties";
import AdminAuctionStats from "@/pages/AdminAuctionStats";
import AdminSmsStats from "@/pages/AdminSmsStats";
import AdminBriefingAbStats from "@/pages/AdminBriefingAbStats";
import PvProjectDetail from "@/pages/PvProjectDetail";
import PvOfferThanks from "@/pages/PvOfferThanks";
import AdminPvCatalog from "@/pages/AdminPvCatalog";
import AdminPvPackages from "@/pages/AdminPvPackages";
import AdminWpPackages from "@/pages/AdminWpPackages";
import AdminWpCatalog from "@/pages/AdminWpCatalog";
import AdminProdukte from "@/pages/AdminProdukte";
import AdminOffers from "@/pages/AdminOffers";
import AdminHierarchy from "@/pages/AdminHierarchy";
import TelesalesCapture from "@/pages/TelesalesCapture";
import TelesalesHome from "@/pages/TelesalesHome";
import TelesalesNextCall from "@/pages/TelesalesNextCall";
import TelesalesDirector from "@/pages/TelesalesDirector";
import AdminCallLists from "@/pages/AdminCallLists";
import AdminPartnerProfile from "@/pages/AdminPartnerProfile";
import AdminSecurityAudit from "@/pages/AdminSecurityAudit";
import AdminCompliance from "@/pages/AdminCompliance";
import AdminAuditScores from "@/pages/AdminAuditScores";
import InvitationAccept from "@/pages/InvitationAccept";
import TenantOnboarding from "@/pages/TenantOnboarding";
import AdminTelesalesSettings from "@/pages/AdminTelesalesSettings";
import EmpfehlungsbonusAnalytics from "@/pages/EmpfehlungsbonusAnalytics";
import AdminSigningOptions from "@/pages/AdminSigningOptions";
import AdminMembershipMargins from "@/pages/AdminMembershipMargins";
import AdminSalesTeams from "@/pages/AdminSalesTeams";
import AdminRoutingRules from "@/pages/AdminRoutingRules";
import { LeadAuctionMarket, LeadAuctionDetail } from "@/pages/LeadAuction";
import ScrollToTop from "@/components/ScrollToTop";
import ProjectList from "@/pages/ProjectList";
import ProjectNew from "@/pages/ProjectNew";
import ProjectDetail from "@/pages/ProjectDetail";
import SketchCanvas from "@/pages/SketchCanvas";
import PhotoAnnotator from "@/pages/PhotoAnnotator";
import AdminHandbook from "@/pages/AdminHandbook";
import AdminQuizResults from "@/pages/AdminQuizResults";
import QuizPage from "@/pages/QuizPage";
import AdminUsers from "@/pages/AdminUsers";
import AdminBranches from "@/pages/AdminBranches";
import AdminPresence from "@/pages/AdminPresence";
import AdminActivity from "@/pages/AdminActivity";
import { UpdateBanner } from "@/components/UpdateBanner";
import { WhatsNewTour } from "@/components/whatsnew/WhatsNewTour";
import AdminTestData from "@/pages/AdminTestData";
import AdminInvoiceTools from "@/pages/AdminInvoiceTools";
import AdminEmailLogs from "@/pages/AdminEmailLogs";
import AdminDocumentTemplates from "@/pages/AdminDocumentTemplates";
import AdminTextBlocks from "@/pages/AdminTextBlocks";
import AdminVCheckRules from "@/pages/AdminVCheckRules";
import AdminTenantModules from "@/pages/AdminTenantModules";
import AdminUsersTable from "@/pages/AdminUsersTable";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminSachbearbeiterStats from "@/pages/AdminSachbearbeiterStats";
import AdminSignatureAudit from "@/pages/AdminSignatureAudit";
import SuperAdminMultiTenantConsolidate from "@/pages/SuperAdminMultiTenantConsolidate";
import SuperAdminForceDelete from "@/pages/SuperAdminForceDelete";
import AdminPackages from "@/pages/AdminPackages";
import AdminInboundLeads from "@/pages/AdminInboundLeads";
import AdminComponents from "@/pages/AdminComponents";
import AdminInstallers from "@/pages/AdminInstallers";
import InstallerUpload from "@/pages/InstallerUpload";
import AdminFeedback from "@/pages/AdminFeedback";
import FeedbackPage from "@/pages/FeedbackPage";
import SalesSettings from "@/pages/SalesSettings";
import UserMessages from "@/pages/UserMessages";
import AdminApiKeys from "@/pages/AdminApiKeys";
import AdminHealth from "@/pages/AdminHealth";
import PublicTrackingPage from "@/pages/PublicTrackingPage";
import ManagerKanban from "@/pages/ManagerKanban";
import BzaKanban from "@/pages/BzaKanban";
import AdminProjectStages from "@/pages/AdminProjectStages";
import AdminManualSignatures from "@/pages/AdminManualSignatures";
import TeamAnalytics from "@/pages/TeamAnalytics";
import RepAnalytics from "@/pages/RepAnalytics";
import CalendarPage from "@/pages/CalendarPage";
import TasksPage from "@/pages/TasksPage";
import HeikoCatalog from "@/pages/HeikoCatalog";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import MyBilling from "@/pages/MyBilling";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import HelpFab from "@/components/HelpFab";
import LegalDocumentPage from "@/pages/LegalDocumentPage";
import LegalAcceptanceGate from "@/components/LegalAcceptanceGate";
import IntakePipeline from "@/pages/IntakePipeline";
import PublicOfferSign from "@/pages/PublicOfferSign";
import SwipeBackGesture from "@/components/SwipeBackGesture";

const Loading = () => (
  <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400">Lade…</div>
);

const Private = ({ children, role }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  // Status-aware routing: users who haven't completed onboarding are confined
  // to a specific page until they progress. `pending` users land on the new
  // plan-selection screen (instead of the legacy /checkout that hard-coded
  // role-based pricing).
  const path = location.pathname;
  // Installer with pending_admin → onboarding wizard (overrides generic pending-admin redirect)
  if (user.role === "installer"
      && user.status === "pending_admin"
      && !path.startsWith("/installer/onboarding")) {
    return <Navigate to="/installer/onboarding" replace />;
  }
  if (user.status === "pending_admin" && path !== "/register/pending-admin") {
    return <Navigate to="/register/pending-admin" replace />;
  }
  // `pending` = Stripe-Abo offen → /select-plan zwingen.
  // EXCEPT: Admins zahlen nie — sie dürfen jede Route besuchen, egal welcher
  // Status historisch im DB-Datensatz steht. So vermeiden wir, dass ein
  // versehentlich auf `pending` gesetztes Admin-Konto sich selbst aussperrt.
  if (user.role !== "admin"
      && user.status === "pending"
      && !path.startsWith("/select-plan")
      && !path.startsWith("/checkout")) {
    return <Navigate to="/select-plan" replace />;
  }
  if (role) {
    // Vertriebsdirektor = Admin-Äquivalent innerhalb des Mandanten (2026-02-14):
    // Wo ein Admin durchgelassen wird, kommt auch der sales_director rein.
    // Admins & super-admins bypass jeden Whitelist-Check.
    const allowed = Array.isArray(role) ? role : [role];
    const bypass = isAdmin(user) || (isDirectorOrAbove(user) && allowed.includes("admin"));
    if (!bypass && !allowed.includes(user.role)) return <Navigate to="/dashboard" replace />;
  }
  return <LegalAcceptanceGate>{children}</LegalAcceptanceGate>;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to={postLoginDestination(user)} replace />;
  return children;
};

// Role-aware index route: signed-in users go to their role-specific home,
// signed-out users land on the public login page.
const RoleAwareIndex = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return <Navigate to={user ? postLoginDestination(user) : "/login"} replace />;
};

function AuthenticatedAssistant() {
  const { user } = useAuth();
  if (!user) return null;
  return <AiAssistant />;
}

function App() {
  return (
    <AuthProvider>
      <TenantCapsProvider>
      <TrainingModeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SwipeBackGesture />
          <TrainingModeBanner />
          <Routes>
          <Route path="/" element={<RoleAwareIndex />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/invitation/:token" element={<InvitationAccept />} />
          <Route path="/onboarding/:token" element={<TenantOnboarding />} />
          <Route path="/o/:token" element={<PublicOfferSign />} />
          <Route path="/register" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register/profile" element={<PublicOnly><RegisterProfile /></PublicOnly>} />
          <Route path="/register/pending-admin" element={<Private><RegisterPendingAdmin /></Private>} />
          <Route path="/m/:token" element={<MagicLinkLogin />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/legal/:slug" element={<LegalDocumentPage />} />
          <Route path="/track/:token" element={<PublicTrackingPage />} />
          <Route path="/installer/:token" element={<InstallerUpload />} />
          <Route path="/demo" element={<DemoTour />} />
          <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
          <Route path="/profile" element={<Private><MyProfile /></Private>} />
          <Route path="/projects" element={<Private><ProjectList /></Private>} />
          <Route path="/projects/new" element={<Private><ProjectNew /></Private>} />
          <Route path="/pipeline/intake" element={<Private role={["admin","sales_director","sales_manager", "branch_manager","super_admin"]}><IntakePipeline /></Private>} />
          <Route path="/projects/:id" element={<Private><ProjectDetail /></Private>} />
          <Route path="/projects/:id/sketch" element={<Private><SketchCanvas /></Private>} />
          <Route path="/projects/:id/photos/:photoId/annotate" element={<Private><PhotoAnnotator /></Private>} />
          <Route path="/admin/handbook" element={<Private><AdminHandbook /></Private>} />
          <Route path="/admin/users" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager", "telesales_director"]}><AdminUsers /></Private>} />
          <Route path="/admin/branches" element={<Private role={["admin", "sales_director", "super_admin"]}><AdminBranches /></Private>} />
          <Route path="/admin/presence" element={<Private role={["admin"]}><AdminPresence /></Private>} />
          <Route path="/admin/activity" element={<Private role={["admin"]}><AdminActivity /></Private>} />
          <Route path="/admin/test-data" element={<Private role={["admin"]}><AdminTestData /></Private>} />
          <Route path="/admin/invoice-tools" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager"]}><AdminInvoiceTools /></Private>} />
          <Route path="/admin/email-logs" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager"]}><AdminEmailLogs /></Private>} />
          <Route path="/admin/document-templates" element={<Private role={["admin", "sales_director", "sales_manager"]}><AdminDocumentTemplates /></Private>} />
          <Route path="/admin/text-blocks" element={<Private role={["admin", "sales_director", "sales_manager"]}><AdminTextBlocks /></Private>} />
          <Route path="/admin/vcheck-pflichtfelder" element={<Private role={["admin", "sales_director", "sales_manager"]}><AdminVCheckRules /></Private>} />
          <Route path="/admin/modules" element={<Private role={["admin"]}><AdminTenantModules /></Private>} />
          <Route path="/admin/users-table" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager", "telesales_director"]}><AdminUsersTable /></Private>} />
          <Route path="/admin/audit-log" element={<Private role={["admin"]}><AdminAuditLog /></Private>} />
          <Route path="/admin/sachbearbeiter-stats" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager"]}><AdminSachbearbeiterStats /></Private>} />
          <Route path="/admin/signature-audit" element={<Private role={["admin", "sales_manager", "branch_manager", "sales_director", "telesales_director"]}><AdminSignatureAudit /></Private>} />
          <Route path="/superadmin/multi-tenant" element={<Private role={["super_admin"]}><SuperAdminMultiTenantConsolidate /></Private>} />
          <Route path="/superadmin/danger" element={<Private role={["super_admin"]}><SuperAdminForceDelete /></Private>} />
          <Route path="/admin/packages" element={<Private role={["admin"]}><AdminPackages /></Private>} />
          <Route path="/admin/orientierungsangebote" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager", "telesales_director"]}><AdminInboundLeads /></Private>} />
          <Route path="/admin/components" element={<Private role={["admin"]}><AdminComponents /></Private>} />
          <Route path="/admin/installers" element={<Private role={["admin"]}><AdminInstallers /></Private>} />
          <Route path="/manager/kanban" element={<Private role={["admin", "sales_manager", "branch_manager"]}><ManagerKanban /></Private>} />
          <Route path="/kanban-bza" element={<Private role={["admin", "sales_manager", "branch_manager", "sales_director", "sales_rep", "backoffice"]}><BzaKanban /></Private>} />
          <Route path="/admin/project-stages" element={<Private role={["admin", "sales_director"]}><AdminProjectStages /></Private>} />
          <Route path="/admin/manual-signatures" element={<Private role={["admin", "sales_director"]}><AdminManualSignatures /></Private>} />
          <Route path="/manager/team" element={<Private role={["admin", "sales_manager", "branch_manager"]}><TeamAnalytics /></Private>} />
          <Route path="/manager/team/:userId" element={<Private role={["admin", "sales_manager", "branch_manager"]}><RepAnalytics /></Private>} />
          <Route path="/feedback" element={<Private><FeedbackPage /></Private>} />
          <Route path="/admin/feedback" element={<Private role={["admin", "sales_manager", "branch_manager"]}><AdminFeedback /></Private>} />
          <Route path="/manager/sales-settings" element={<Private role={["admin", "sales_director", "super_admin"]}><SalesSettings /></Private>} />
          <Route path="/messages/user/:userId" element={<Private><UserMessages /></Private>} />
          <Route path="/admin/api-keys" element={<Private role={["admin"]}><AdminApiKeys /></Private>} />
          <Route path="/admin/health" element={<Private role={["admin", "sales_director", "sales_manager", "branch_manager"]}><AdminHealth /></Private>} />
          <Route path="/calendar" element={<Private><CalendarPage /></Private>} />
          <Route path="/tasks" element={<Private><TasksPage /></Private>} />
          <Route path="/katalog" element={<Private role={["admin", "sales_rep", "sales_manager", "branch_manager", "sales_director"]}><HeikoCatalog /></Private>} />
          <Route path="/checkout" element={<Private><Checkout /></Private>} />
          <Route path="/select-plan" element={<Private><SelectPlan /></Private>} />
          <Route path="/checkout/success" element={<Private><CheckoutSuccess /></Private>} />
          <Route path="/abo" element={<Private><MyBilling /></Private>} />
          <Route path="/admin/promo-codes" element={<Private role="admin"><AdminPromoCodes /></Private>} />
          <Route path="/quiz" element={<Private><QuizPage /></Private>} />
          <Route path="/admin/quiz-results" element={<Private role={["admin", "sales_director"]}><AdminQuizResults /></Private>} />
          <Route path="/admin/tenants" element={<Private role={["admin"]}><SuperAdminTenants /></Private>} />
          <Route path="/admin/back-office" element={<Private role={["admin"]}><BackOffice /></Private>} />
          {/* Legacy: /admin/wp-addons war die alte "WP-Zusatzleistungen"-Seite — vollständig durch wp-components abgelöst. */}
          <Route path="/admin/wp-addons" element={<Navigate to="/admin/wp-components" replace />} />
          <Route path="/admin/produkte" element={<Private role={["admin","sales_director"]}><AdminProdukte /></Private>} />
          <Route path="/admin/wp-catalog" element={<Navigate to="/admin/produkte?tab=wp-katalog" replace />} />
          <Route path="/admin/pv-catalog" element={<Navigate to="/admin/produkte?tab=pv-katalog" replace />} />
          <Route path="/admin/pv-packages" element={<Navigate to="/admin/produkte?tab=pv-pakete" replace />} />
          <Route path="/admin/wp-packages" element={<Navigate to="/admin/produkte?tab=wp-pakete" replace />} />
          <Route path="/admin/wp-components" element={<Navigate to="/admin/produkte?tab=wp-komponenten" replace />} />
          <Route path="/admin/system-health" element={<Private role={["admin","sales_director"]}><AdminSystemHealth /></Private>} />
          <Route path="/admin/offers" element={<Private role={["admin","sales_director","telesales_director"]}><AdminOffers /></Private>} />
          <Route path="/admin/hierarchy" element={<Private role={["admin","sales_director"]}><AdminHierarchy /></Private>} />
          <Route path="/telesales" element={<Private role={["agent","admin","telesales_director"]}><TelesalesHome /></Private>} />
          <Route path="/telesales/capture" element={<Private role={["agent","admin","telesales_director"]}><TelesalesCapture /></Private>} />
          <Route path="/telesales/next-call" element={<Private role={["agent"]}><TelesalesNextCall /></Private>} />
          <Route path="/telesales/director" element={<Private role={["telesales_director","admin"]}><TelesalesDirector /></Private>} />
          <Route path="/admin/call-lists" element={<Private role={["admin","telesales_director"]}><AdminCallLists /></Private>} />
          <Route path="/admin/partner-profiles" element={<Private role={["admin"]}><AdminPartnerProfile /></Private>} />
          <Route path="/admin/security-audit" element={<Private role={["admin"]}><AdminSecurityAudit /></Private>} />
          <Route path="/admin/compliance" element={<Private role={["admin"]}><AdminCompliance /></Private>} />
          <Route path="/admin/audit-scores" element={<Private role={["admin"]}><AdminAuditScores /></Private>} />
          <Route path="/admin/telesales-settings" element={<Private role={["admin"]}><AdminTelesalesSettings /></Private>} />
          <Route path="/admin/empfehlungsbonus-analytics" element={<Private role={["admin", "sales_director"]}><EmpfehlungsbonusAnalytics /></Private>} />
          <Route path="/admin/signing-options" element={<Private role={["super_admin", "admin"]}><AdminSigningOptions /></Private>} />
          <Route path="/super-admin/membership-margins" element={<Private role={["super_admin"]}><AdminMembershipMargins /></Private>} />
          <Route path="/admin/sales-teams" element={<Private role={["admin"]}><AdminSalesTeams /></Private>} />
          <Route path="/admin/routing-rules" element={<Private role={["admin"]}><AdminRoutingRules /></Private>} />
          <Route path="/lead-auctions" element={<Private><LeadAuctionMarket /></Private>} />
          <Route path="/lead-auctions/:id" element={<Private><LeadAuctionDetail /></Private>} />
          <Route path="/admin/installer-applications" element={<Private role={["admin", "sales_director"]}><AdminInstallerApplications /></Private>} />
          <Route path="/admin/penalties" element={<Private role={["admin", "sales_director"]}><AdminPenalties /></Private>} />
          <Route path="/admin/auction-stats" element={<Private role={["admin", "sales_director"]}><AdminAuctionStats /></Private>} />
          <Route path="/admin/sms-stats" element={<Private role={["admin", "sales_director"]}><AdminSmsStats /></Private>} />
          <Route path="/admin/briefing-ab-stats" element={<Private role={["admin", "sales_director"]}><AdminBriefingAbStats /></Private>} />
          <Route path="/projects/:id/pv" element={<Private><PvProjectDetail /></Private>} />
          <Route path="/pv-offer/thanks" element={<PvOfferThanks />} />
          <Route path="/installer/onboarding" element={<Private role={["installer"]}><InstallerOnboarding /></Private>} />
          <Route path="/installer/auctions" element={<Private role={["installer", "admin"]}><InstallerAuctionsList /></Private>} />
          <Route path="/installer/auctions/:id" element={<Private role={["installer", "admin"]}><InstallerAuctionDetail /></Private>} />
          <Route path="/installer/jobs" element={<Private role={["installer", "admin"]}><InstallerJobs /></Private>} />
          {/* Legacy URL — same page, kept for any bookmarks. */}
          <Route path="/superadmin/tenants" element={<Navigate to="/admin/tenants" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <AuthenticatedAssistant />
        <OnlineStatusBar />
        <HelpFab />
        <UpdateBanner />
        <WhatsNewTour />
        <FeedbackFab />
        <Toaster
          theme="dark"
          richColors
          position="top-center"
          toastOptions={{
            style: { background: "#18181B", border: "1px solid #3F3F46", color: "#FAFAFA" },
          }}
        />
        </BrowserRouter>
      </TrainingModeProvider>
      </TenantCapsProvider>
    </AuthProvider>
  );
}

export default App;
